import type { UseFormSetError } from 'react-hook-form';
import type { User } from '@/utils/types/User';
import { type Renter } from '../../utils/types/renter.interface';

import { useEffect, useMemo, useState } from 'react';
import LoadingButton from '@mui/lab/LoadingButton';
import { Alert, Box, Tooltip } from '@mui/material';

import { Divider } from '@/components/Divider';
import { checkIfPmi } from '@/utils/commonUtils';
import {
  MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE,
  SELF_ENROLLED_BUILDING_ATTR,
} from '@/utils/constants';
import { RentCoverageTypes, SecurityDepositTypes } from '../../constants/CoverageType';
import { CoverageRequirementsAlertV2 } from './CoverageRequirementsAlertV2';
import CoverageSelectionV2 from './CoverageSelectionV2';
import { LeaseInformationV2 } from './LeaseInformationV2';
import { RenterContactV2 } from './RenterContactV2';
import { ScreeningResults } from './ScreeningResults';
import ScreeningResultsV2 from './ScreeningResultsV2';
import useRenterInviteForm from './useRenterInviteForm';
import { parseMoney } from '@/utils/ParseMoney';
import { theme } from '@theguarantors/tg-package-material-ui';
import {
  calculateGrossTotalCoverage,
  calculateLeaseDurationMonths,
  calculateMonthsOfCoverage,
  coverageMonthCountOptions,
  isCoverageOptionEnabled,
} from './utils/coverage.util';

interface RenterInviteDrawerFormProps {
  onSubmit: (values: Renter, setError: UseFormSetError<Renter>) => void;
  errorMsg: string;
  user?: User;
  newCoveragesEnabled?: boolean;
  customCoverageDisabled?: boolean;
  isSmbRenterInvites?: boolean;
}

const RenterInviteDrawerForm = ({
  onSubmit,
  errorMsg,
  user,
  newCoveragesEnabled,
  customCoverageDisabled = false,
  isSmbRenterInvites = true,
}: RenterInviteDrawerFormProps) => {
  const [isSelfEnrolled, setIsSelfEnrolled] = useState<boolean>(false);

  const selfEnrolledSDRLimit = useMemo(() => {
    if (!user) return;
    if (checkIfPmi(user)) {
      return MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE.PMI;
    }
    if (isSelfEnrolled) {
      return MONTHLY_RENT_SDR_LIMIT_BY_USER_TYPE.SELF_ENROLLED;
    }
  }, [user, isSelfEnrolled]);

  const {
    handleSubmit,
    watch,
    control,
    trigger,
    setError,
    resetField,
    setValue,
    formState: { isSubmitting, isValid, errors },
  } = useRenterInviteForm({
    selfEnrolledSDRLimit,
    customCoverageDisabled,
    useNewCoveragesOnLandlordInvite: newCoveragesEnabled === true,
  });

  const customCoverage = watch('customCoverage');
  const address = watch('address');

  useEffect(() => {
    if (customCoverage) {
      resetField('approvalStatus');
      setValue('rentCoverage', RentCoverageTypes.Customize);
      setValue('securityDepositCoverage', SecurityDepositTypes.CustomizeOrRemove);
    } else {
      setValue('rentCoverage', RentCoverageTypes.CompanyDefault);
      setValue('securityDepositCoverage', SecurityDepositTypes.CompanyDefault);
    }
  }, [customCoverage, resetField, setValue]);

  useEffect(() => {
    setIsSelfEnrolled(address?.source === SELF_ENROLLED_BUILDING_ATTR);
  }, [address]);

  const leaseStart = watch('leaseStart');
  const leaseEnd = watch('leaseEnd');
  const leaseDurationMonths = calculateLeaseDurationMonths(leaseStart, leaseEnd);
  const monthsOfCoverage = watch('monthsOfCoverage');

  // Reset monthsOfCoverage if the selected option becomes disabled due to lease duration change
  useEffect(() => {
    if (newCoveragesEnabled && monthsOfCoverage && !customCoverage) {
      const coverageValue =
        typeof monthsOfCoverage === 'string' ? Number(monthsOfCoverage) : monthsOfCoverage;
      const isCurrentSelectionEnabled = isCoverageOptionEnabled(coverageValue, leaseDurationMonths);
      if (!isCurrentSelectionEnabled) {
        setValue('monthsOfCoverage', undefined);
      }
    }
  }, [customCoverage, leaseDurationMonths, monthsOfCoverage, newCoveragesEnabled, setValue]);

  const grossTotalCoverage = calculateGrossTotalCoverage(
    watch('monthsOfCoverage'),
    watch('grossMonthlyRent'),
    watch('freeRent'),
    watch('prepaidRent'),
  );
  const netMonthsOfCoverage = calculateMonthsOfCoverage(
    watch('grossMonthlyRent'),
    grossTotalCoverage,
  );

  return (
    <form
      id={'renter-invite-form'}
      style={{ margin: 0 }}
      onSubmit={handleSubmit((values) =>
        onSubmit(
          newCoveragesEnabled
            ? {
                ...values,
                monthsOfCoverage: netMonthsOfCoverage,
              }
            : values,
          setError,
        ),
      )}
      noValidate
    >
      {customCoverageDisabled && <CoverageRequirementsAlertV2 />}
      <RenterContactV2 control={control} errors={errors} watch={watch} />
      <Divider sx={{ borderColor: `${theme.palette.neutralGrey[200]} !important`, my: 3 }} />
      <LeaseInformationV2
        control={control}
        errors={errors}
        watch={watch}
        setValue={setValue}
        trigger={trigger}
      />
      <Divider sx={{ borderColor: `${theme.palette.neutralGrey[200]} !important`, my: 3 }} />

      {newCoveragesEnabled ? (
        <ScreeningResultsV2
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
          isSmbInvites={isSmbRenterInvites}
          useDrawerStyles={true}
          coverageLengthOptions={coverageMonthCountOptions.map((monthCount) => {
            const grossCoverage = calculateGrossTotalCoverage(
              monthCount,
              watch('grossMonthlyRent'),
              watch('freeRent'),
              watch('prepaidRent'),
            );

            const isEnabledByLeaseDuration = isCoverageOptionEnabled(
              monthCount,
              leaseDurationMonths,
            );

            return {
              id: `coverage-${monthCount}-months`,
              value: monthCount.toString(),
              disabled: !isEnabledByLeaseDuration,
              text: `${monthCount.toString()} months`,
              subText: watch('grossMonthlyRent')
                ? `${parseMoney(grossCoverage) || '$0'} max coverage`
                : '$0 max coverage',
            };
          })}
        />
      ) : (
        <ScreeningResults
          control={control}
          errors={errors}
          watch={watch}
          setValue={setValue}
          isSmbInvites={isSmbRenterInvites}
        />
      )}
      {((isSmbRenterInvites && !!customCoverage) ||
        (!isSmbRenterInvites && !customCoverageDisabled)) && (
        <>
          <Divider sx={{ borderColor: `${theme.palette.neutralGrey[200]} !important`, my: 3 }} />
          <CoverageSelectionV2
            control={control}
            errors={errors}
            watch={watch}
            useDrawerStyles={true}
            selfEnrolledSDRLimit={selfEnrolledSDRLimit}
          />
        </>
      )}

      {errorMsg && (
        <Alert severity="error" sx={{ marginY: 2 }}>
          {errorMsg}
        </Alert>
      )}
      <Box
        sx={{
          position: 'sticky',
          bottom: -1,
          backgroundColor: 'background.paper',
          borderTop: `1px solid ${theme.palette.border?.primary}`,
          pt: 2,
          pb: 2,
          mt: 2,
        }}
      >
        <Tooltip title={!isValid ? 'Please complete the required fields to proceed' : ''}>
          <Box
            sx={{
              display: { xs: 'inherit', sm: 'inline-block' },
              [theme.breakpoints.up('sm')]: newCoveragesEnabled
                ? { width: '100%', height: '55px' }
                : {},
            }}
          >
            <LoadingButton
              variant="contained"
              type="submit"
              disabled={!isValid || isSubmitting}
              loading={isSubmitting}
              id={'RenterInviteSubmitButton'}
              style={!isValid || isSubmitting ? { pointerEvents: 'none' } : {}}
              sx={{
                [theme.breakpoints.up('sm')]: newCoveragesEnabled
                  ? { width: '100%', height: '55px' }
                  : {},
              }}
            >
              Send Invite
            </LoadingButton>
          </Box>
        </Tooltip>
      </Box>
    </form>
  );
};

export default RenterInviteDrawerForm;
